export const WHATSAPP_SETUP_STATUSES = [
  'not_started',
  'incomplete',
  'setup_in_progress',
  'setup_incomplete',
  'awaiting_waba_selection',
  'connected',
  'waba_inaccessible',
] as const;

export type WhatsAppSetupStatus = (typeof WHATSAPP_SETUP_STATUSES)[number];

// Meta's phone-number `name_status`; the wire value is a free string, these are the documented ones.
export type WhatsAppNameStatus =
  | 'APPROVED'
  | 'AVAILABLE_WITHOUT_REVIEW'
  | 'PENDING_REVIEW'
  | 'DECLINED'
  | 'EXPIRED'
  | 'NONE';

export type WhatsAppPhoneNumberEntry = {
  phone_number_id: string;
  display_phone_number: string;
  verified_name: string;
  quality_rating: string;
  is_active: boolean;
  name_status?: WhatsAppNameStatus | string | null;
  account_mode?: string | null;
  code_verification_status?: string | null;
  platform_type?: string | null;
  is_test_number?: boolean;
};

export type WhatsAppIntegrationInfo = {
  whatsapp_id: string;
  account_name: string | null;
  created_timestamp: string;
  webhook_subscribed: boolean;
  admin_assigned: boolean;
  business_id: string | null;
};

export type WhatsAppPhoneNumbersListResponse = {
  integration_info: WhatsAppIntegrationInfo;
  phone_numbers: WhatsAppPhoneNumberEntry[];
};

export type WhatsAppSendErrorCategory =
  | 'display_name_approval'
  | 'token_invalid'
  | 'waba_inaccessible'
  | 'number_quality'
  | 'generic';

export type WhatsAppSendHealthError = {
  category: WhatsAppSendErrorCategory;
  meta_error_code?: number | null;
  occurred_at?: string | null;
};

export type WhatsAppNumberActivation = {
  phone_number_id: string;
  display_phone_number: string;
  status: 'awaiting_inbound' | 'awaiting_reply' | 'verified' | 'test_number' | 'blocked';
};

export type WhatsAppSetupStatusResponse = {
  status: WhatsAppSetupStatus;
  has_whatsapp_id: boolean;
  has_phone_number: boolean;
  webhook_subscribed: boolean;
  admin_assigned: boolean;
  issues: string[];
  waba_id?: string | null;
  send_error?: WhatsAppSendHealthError | null;
  // A visitor-authored WhatsApp message has reached this AI Agent; outbound-only sends do not count.
  has_inbound_message?: boolean;
  activation_status?: 'not_ready' | 'awaiting_exchange' | 'live' | 'needs_attention';
  activation_numbers?: WhatsAppNumberActivation[];
};

export type WhatsAppOauthStateResponse = {
  state: string;
  state_fingerprint: string;
  attempt_id?: string | null;
};

export type WhatsAppOauthResultResponse = {
  status: 'pending' | 'success' | 'error';
  reason_code: string | null;
  waba_stage_id?: string | null;
};

export type WhatsAppPendingWabaPhoneNumber = {
  display_phone_number: string;
  verified_name: string;
};

export type WhatsAppPendingWaba = {
  whatsapp_id: string;
  account_name: string;
  phone_numbers: WhatsAppPendingWabaPhoneNumber[];
  stage_id: string;
  is_expired: boolean;
};

export type WhatsAppWabaSelectResponse = {
  status: 'success' | 'error';
  reason_code: string;
};
